import { useCallback } from 'react';

import { AxiosResponse } from 'axios';
import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import { PaginatedWorkoutHistoryRowList, UrlParams } from 'types';
import { client } from 'utils/api-client';
import { encodeUrlParams, urlParamsFromUrl } from 'utils/functions';

interface QueryKeyType {
  pageParam?: UrlParams;
}

interface IUseWorkoutHistory {
  athleteId?: string;
  search?: string;
  type?: string | null;
  startDateRange?: string | null;
  endDateRange?: string | null;
  ordering?: string;
  limit?: string;
  enabled?: boolean;
}

function useWorkoutHistory({
  athleteId,
  search,
  type,
  startDateRange,
  endDateRange,
  ordering,
  limit = '20',
  enabled = true,
}: IUseWorkoutHistory): UseInfiniteQueryResult<AxiosResponse<PaginatedWorkoutHistoryRowList>> {
  const fetchWorkoutHistory = useCallback(
    ({
      pageParam = { limit, offset: '0' },
    }: QueryKeyType): Promise<AxiosResponse<PaginatedWorkoutHistoryRowList>> => {
      const params = { ...pageParam };
      if (athleteId) {
        params.athlete = athleteId;
      }
      if (search) {
        params.search = search;
      }
      if (type) {
        params.type = type;
      }
      if (startDateRange) {
        params.start_date_range = startDateRange;
      }
      if (endDateRange) {
        params.end_date_range = endDateRange;
      }
      if (ordering) {
        params.ordering = ordering;
      }
      return client.get(`workout-history/?${encodeUrlParams(params)}`);
    },
    [athleteId, endDateRange, limit, ordering, search, startDateRange, type],
  );

  return useInfiniteQuery(
    ['workout-history', athleteId, search, type, startDateRange, endDateRange, ordering, limit],
    fetchWorkoutHistory,
    {
      enabled: enabled && !!athleteId,
      getNextPageParam: page => {
        return page.data.next ? urlParamsFromUrl(page.data.next) : undefined;
      },
    },
  );
}

export default useWorkoutHistory;
